import { parseRequestError, parseBlobRequestError } from '@/common/utilities.ts'
import axios from 'axios'
import { z } from 'zod'
import {
  DocumentCategorySchema,
  DocumentVariantSchema,
} from './documents.schema.ts'
import { ReasoningInputSchema } from '@/pages/applicants/applicant_details/hooks/useSubmitFlagDecision.tsx'

export const ApplicationStatusSchema = z.number().transform((val) => {
  switch (val) {
    case 1:
      return 'Invited' as const
    case 2:
      return 'In Progress' as const
    case 3:
      return 'Pending' as const
    case 4:
      return 'Eligible' as const
    case 5:
      return 'In Review' as const
    case 6:
      return 'Ineligible' as const
    case 7:
      return 'Complete' as const
    case 8:
      return 'Awarded' as const
    case 9:
      return 'Appeal' as const
    case 10:
      return 'Funded' as const
    case 11:
      return 'Wait Listed' as const
    case 12:
      return 'Age Phase Out' as const
    case 13:
      return 'V1 Invited' as const
    case 14:
      return 'ClassWallet Renewal' as const
    case 15:
      return 'SoftPoint Renewal' as const
    case 16:
      return 'Withdrawn' as const
    default:
      throw new Error(`Invalid application status code: ${val}`)
  }
})

export type ApplicationStatus = z.infer<typeof ApplicationStatusSchema>

export const SHOULD_DISPLAY_PURSE_STATUS: readonly ApplicationStatus[] = [
  'Eligible',
  'Funded',
  'Awarded',
]

const StudentProgramPurseSchema = z.object({
  id: z.number(),
  name: z.string(),
  laravel_through_key: z.number(),
  current_award: z.number().optional().default(0),
  total_award: z.number().optional().default(0),
})

const StudentProgramPursesSchema = z.array(StudentProgramPurseSchema).optional()

const FamilyApplicationSchema = z
  .object({
    id: z.number(),
    applicant_id: z.number(),
    status: ApplicationStatusSchema,
    inception_status: z.number(),
    family_account_id: z.number(),
    application_id: z.number(),
    student_id: z.number().nullable(),
    student_first_name: z.string().nullable(),
    student_last_name: z.string().nullable(),
    student_sequence_number: z.string().nullable(),
    student_grade: z.string().nullable().optional(),
    applicant_first_name: z.string().nullable(),
    applicant_last_name: z.string().nullable(),
    last_datetime: z.string().nullable(),
    applicant_email: z.string(),
    assestment: z.any().nullable().optional(),
    sub_status: z.number(),
    total_awarded_amount: z.number().or(z.undefined()),
    last_awarded_at: z.string().or(z.undefined()),
    is_funded_current_term: z.boolean().optional(),
    is_preview_awarded_current_term: z.boolean().optional(),
    program_purses: StudentProgramPursesSchema,
    balance_available: z.number(),
  })
  .transform((studentApplication) => {
    let statusName: ApplicationStatusWithSubStatus = studentApplication.status
    if (studentApplication.status === 'Eligible') {
      switch (studentApplication.sub_status) {
        case 1:
          statusName = 'Eligible'
          break
        case 2:
          statusName = 'Selected'
          break
        default:
          throw new Error('Invalid sub-status code')
      }
    }
    if (studentApplication.status === 'Ineligible') {
      switch (studentApplication.sub_status) {
        case 1:
          statusName = 'Ineligible'
          break
        case 2:
          statusName = 'Ineligible (Duplicate)'
          break
        default:
          throw new Error('Invalid sub-status code')
      }
    }
    return {
      ...studentApplication,
      sub_status: statusName,
    }
  })

export const InceptionStatus = {
  New: 0,
  Waitlist: 1,
  Renewal: 2,
  FamilyRenewal: 3,
} as const

export const getInceptionStatusText = (status: number): string => {
  switch (status) {
    case 0:
      return 'New'
    case 1:
      return 'Waitlist'
    case 2:
      return 'Renewal'
    case 3:
      return 'Family Renewal'
    default:
      return '--'
  }
}

type FamilyApplicationStatusText =
  | 'Invited'
  | 'In Review'
  | 'Complete'
  | 'In Progress'
  | 'Eligible'
  | 'Ineligible'
  | 'Pending'
  | 'Appeal'
  | 'Appeal Denied'
  | 'Appeal Accepted'
  | 'Awarded'

export const getFamilyApplicationStatusText = (
  status: number
): FamilyApplicationStatusText => {
  switch (status) {
    case 1:
      return 'Invited'
    case 2:
      return 'In Progress'
    case 3:
      return 'Pending'
    case 4:
      return 'Eligible'
    case 5:
      return 'In Review'
    case 6:
      return 'Ineligible'
    case 7:
      return 'Complete'
    case 8:
      return 'Awarded'
    case 9:
      return 'Appeal'
    case 10:
      return 'Appeal Denied'
    case 11:
      return 'Appeal Accepted'
    default:
      return 'In Progress'
  }
}

export function getStudentLeaText(value: number) {
  switch (value) {
    case 1:
      return 'Yes'
    case 2:
      return 'No'
    default:
      return 'Not sure'
  }
}

export const FamilyApplicationPaginatedSchema = z.object({
  current_page: z.number(),
  data: z.array(FamilyApplicationSchema),
  first_page_url: z.string(),
  from: z.number(),
  last_page: z.number(),
  last_page_url: z.string(),
  links: z.array(
    z.union([
      z.object({ url: z.null(), label: z.string(), active: z.boolean() }),
      z.object({ url: z.string(), label: z.string(), active: z.boolean() }),
    ])
  ),
  next_page_url: z.string().nullable(),
  path: z.string(),
  per_page: z.number(),
  prev_page_url: z.string().nullable(),
  to: z.number(),
  total: z.number(),
})

export type FamilyApplication = z.infer<typeof FamilyApplicationSchema>

export type FilterStatusOption = number[]

export type FamilyApplicationList = FamilyApplication[]
export type PaginatedFamilyApplications = z.infer<
  typeof FamilyApplicationPaginatedSchema
>

export interface GetPaginatedFamilyApplicationsParams {
  currentPage: number
  programId?: number
  recordsPerPage?: number
  search?: string
  selectedFilter?: string
  sort_by?: string
  sort_order?: string
  only_students?: 0 | 1
}

export const getPaginatedProgramFamilyApplications = async function ({
  currentPage,
  programId,
  recordsPerPage = 20,
  search,
  selectedFilter,
  sort_by,
  sort_order,
  only_students,
}: GetPaginatedFamilyApplicationsParams): Promise<PaginatedFamilyApplications> {
  try {
    const params: {
      program_id?: number
      paginate: number
      per_page: number
      page: number
      search?: string
      status_in?: string
      sort_by?: string
      sort_order?: string
      only_students?: 0 | 1
    } = {
      program_id: programId,
      paginate: 1,
      per_page: recordsPerPage,
      page: currentPage,
      search: search,
      status_in: selectedFilter,
      ...(sort_by && { sort_by }),
      ...(sort_order && { sort_order }),
      ...(only_students !== 0 && { only_students }),
    }

    const response = await axios.get('family_application', {
      params,
    })
    return FamilyApplicationPaginatedSchema.parse(response.data)
  } catch (error) {
    throw new Error(parseRequestError(error).message)
  }
}

export interface ExportFamilyApplicationsParams {
  programId: number
  search?: string
  statusIn?: string
}

export const exportFamilyApplications = async ({
  programId,
  search,
  statusIn,
}: ExportFamilyApplicationsParams): Promise<Blob> => {
  try {
    const response = await axios.get('family_application/export', {
      responseType: 'blob',
      params: {
        program_id: programId,
        ...(search && { search }),
        ...(statusIn && { status_in: statusIn }),
      },
    })

    return response.data
  } catch (error) {
    throw new Error((await parseBlobRequestError(error)).message)
  }
}

const ProviderSummarySchema = z.object({
  id: z.number(),
  name: z.string().nullable(),
})

const ProvidersSchema = z.array(ProviderSummarySchema)

export type ApplicationStatusWithSubStatus =
  | ApplicationStatus
  | 'Selected'
  | 'Ineligible (Duplicate)'
const StudentSchema = z
  .object({
    id: z.number(),
    inception_status: z.number().nullable().optional(),
    scholarship_applications_status: ApplicationStatusSchema,
    scholarship_applications_sub_status: z.number(),
    status: z.number(),
    is_external_student: z.coerce.boolean(),
    name: z.string().nullable(),
    date_of_birth: z.string().nullable(),
    SSN: z.string().nullable(),
    grade: z.string().nullable(),
    priority_group: z.number().nullable(),
    relationship: z.string().nullable(),
    require_special_education: z.number().nullable(),
    lea_participation: z.number().nullable(),
    competing_scholarship_ids: z.array(z.number()).nullable(),
    education_plan: z.number().nullable(),
    SSID: z.string().nullable(),
    achievement_tracking_option_id: z.number().nullable(),
    achievement_verification_result: z.number(),
    achievement_verification_result_text: z.string(),
    birth_certificate_path: z.string().nullable(),
    withdrawn_by: z.string().nullable(),
    scholarship_applications_id: z.number(),
    sequence_number: z.string().nullable(),
    student_program_purses: StudentProgramPursesSchema,
    providers: ProvidersSchema,
  })
  .transform((student) => {
    let statusName: ApplicationStatusWithSubStatus =
      student.scholarship_applications_status
    if (student.scholarship_applications_status === 'Eligible') {
      switch (student.scholarship_applications_sub_status) {
        case 1:
          statusName = 'Eligible'
          break
        case 2:
          statusName = 'Selected'
          break
        default:
          throw new Error('Invalid sub-status code')
      }
    }
    if (student.scholarship_applications_status === 'Ineligible') {
      switch (student.scholarship_applications_sub_status) {
        case 1:
          statusName = 'Ineligible'
          break
        case 2:
          statusName = 'Ineligible (Duplicate)'
          break
        default:
          throw new Error('Invalid sub-status code')
      }
    }
    return {
      ...student,
      scholarship_applications_sub_status: statusName,
    }
  })

export type Student = z.infer<typeof StudentSchema>

const OtherStudents = z
  .object({
    id: z.number(),
    status: z.number(),
    inception_status: z.number().nullable().optional(),
    is_external_student: z.coerce.boolean(),
    name: z.string().nullable(),
    date_of_birth: z.string().nullable(),
    grade: z.string().nullable(),
    priority_group: z.number().nullable(),
    relationship: z.string().nullable(),
    require_special_education: z.number().nullable(),
    lea_participation: z.number().nullable(),
    scholarship_applications_id: z.number(),
    scholarship_applications_status: ApplicationStatusSchema,
    scholarship_applications_sub_status: z.number(),
    competing_scholarship_ids: z.array(z.number()).nullable(),
    education_plan: z.number().nullable(),
    SSID: z.string().nullable(),
    achievement_tracking_option_id: z.number().nullable(),
    achievement_verification_result: z.number(),
    achievement_verification_result_text: z.string(),
    birth_certificate_path: z.string().nullable(),
    withdrawn_by: z.string().nullable(),
    sequence_number: z.string().nullable(),
    student_program_purses: StudentProgramPursesSchema,
    providers: ProvidersSchema,
  })
  .transform((student) => {
    let statusName: ApplicationStatusWithSubStatus =
      student.scholarship_applications_status
    if (student.scholarship_applications_status === 'Eligible') {
      switch (student.scholarship_applications_sub_status) {
        case 1:
          statusName = 'Eligible'
          break
        case 2:
          statusName = 'Selected'
          break
        default:
          throw new Error('Invalid sub-status code')
      }
    }
    if (student.scholarship_applications_status === 'Ineligible') {
      switch (student.scholarship_applications_sub_status) {
        case 1:
          statusName = 'Ineligible'
          break
        case 2:
          statusName = 'Ineligible (Duplicate)'
          break
        default:
          throw new Error('Invalid sub-status code')
      }
    }
    return {
      ...student,
      scholarship_applications_sub_status: statusName,
    }
  })

const FamilyAttestationSchema = z.object({
  order: z.number(),
  id: z.number(),
  status: z.number(),
  program_attestation_id: z.number(),
  accepted_time: z.string(),
})

const FamilyDocumentsSchema = z.object({
  id: z.number(),
  type: DocumentCategorySchema,
  variation: DocumentVariantSchema,
  image_name_front: z.string().optional(),
  created_datetime: z.string(),
  first_name: z.string().optional(),
  last_name: z.string().optional(),
  document_owner: z.string().optional(),
})

const StudentsDocumentsSchema = z.object({
  id: z.number(),
  variation: z.string(),
  first_name: z.string(),
  last_name: z.string(),
  image_name_front: z.string().optional(),
  created_datetime: z.string(),
})
export type FamilyAttestation = z.infer<typeof FamilyAttestationSchema>
export type FamilyDocuments = z.infer<typeof FamilyDocumentsSchema>
export type StudentsDocuments = z.infer<typeof StudentsDocumentsSchema>

export type StudentOfApplicant = z.infer<typeof StudentSchema>

export const FlagStatusSchema = z.number().transform((val) => {
  switch (val) {
    case 0:
      return 'Pending'
    case 1:
      return 'Approved'
    case 2:
      return 'Rejected'
    default:
      throw new Error(`Invalid application flag status code: ${val}`)
  }
})

export type FlagStatus = z.infer<typeof FlagStatusSchema>

const ApplicationFlag = z.object({
  id: z.number(),
  status: FlagStatusSchema,
  student_id: z.number().nullable(),
  flag_description: z.string().nullable(),
  flag_name: z.string(),
  flag_code: z.string(),
  justification: z.string().nullable(),
  reasoning: z.number(),
  reasoning_name: ReasoningInputSchema,
})

export type ApplicationFlag = z.infer<typeof ApplicationFlag>

const FamilyApplicationDetailsSchema = z.object({
  id: z.number(),
  status: ApplicationStatusSchema,
  name: z.string().nullable(),
  spouse_name: z.string().nullable(),
  spouse_last_name: z.string().nullable(),
  email: z.string(),
  phone: z.string().nullable(),
  is_military_exemption: z.number(),
  address: z.string().nullable(),
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  address2: z.string().nullable(),
  city: z.string().nullable(),
  county: z.string().nullable(),
  SSN: z.string().nullable(),
  date_of_birth: z.string().nullable(),
  inception_status: z.number(),
  zip_code: z.string().nullable(),
  global_state_id: z.number().nullable(),
  language_preference_id: z.number().nullable().optional(),
  household_inhabitants: z.number().nullable(),
  submitted_at: z.string().nullable().or(z.undefined()),
  household_income_greater: z.number().nullable(),
  household_income_type: z.string().nullable(),
  other_students: z.array(OtherStudents).nullable(),
  non_taxable_income: z.array(z.string()).nullable(),
  household_filed_federal_income: z.string().nullable(),
  income: z.number().nullable(),
  spouse_income: z.number().nullable(),
  user_id: z.number().nullable(),
  application_id: z.number().nullable(),
  students: StudentSchema.nullable(),
  contact_preference: z.number().nullable().optional(),
  verified_income_tax_year: z.string().nullable(),
  verified_by: z.string().nullable(),
  family_attestations: z.array(FamilyAttestationSchema).nullable(),
  flags: z.array(ApplicationFlag).min(0),
  family_documents: z.array(FamilyDocumentsSchema).optional(),
  students_documents: z.array(StudentsDocumentsSchema),
  spouse_verified_tax_year: z.string().nullable(),
})

export type OtherStudents = z.infer<typeof OtherStudents>

export type FamilyApplicationDetails = z.infer<
  typeof FamilyApplicationDetailsSchema
>

export const getFamilyApplication = async (
  scholarshipApplicationId?: string,
  programId?: number
) => {
  const response = await axios.get(
    `family_application/${scholarshipApplicationId}`,
    {
      params: {
        program_id: programId,
      },
    }
  )
  return FamilyApplicationDetailsSchema.parse(response.data)
}

export const AwardDetailSchema = z.object({
  student_id: z.number(),
  sequence_number: z.string().nullable(),
  balance_available: z.number().nullable(),
  awarded_amount: z.number().nullable(),
  awarded_year_initial: z.string().nullable(),
})

export const AwardDetailsArraySchema = z.array(AwardDetailSchema)

export type AwardDetail = z.infer<typeof AwardDetailSchema>

export const getAwardDetails = async (
  scholarshipApplicationId: string,
  programId: number
): Promise<AwardDetail[]> => {
  try {
    const response = await axios.get(
      `family_application/${scholarshipApplicationId}/award_details`,
      {
        params: {
          program_id: programId,
        },
      }
    )
    return AwardDetailsArraySchema.parse(response.data)
  } catch (error) {
    throw new Error(parseRequestError(error).message)
  }
}
